import { FC, useEffect, useState } from 'react';

import { Button } from 'antd';
import { motion } from 'framer-motion';

import Alert from 'components/Alert/Alert';
import InterviewSchedulerModal from 'components/InterviewSchedulerModal/InterviewSchedulerModal';

import { useInterviewConfirmation } from 'hooks/useInterviewConfirmation';

import { convertSlotsTimeZone } from 'utils/dateUtils';

import {
  InterviewDetails,
  InterviewSlotSerialized
} from 'types/interviewSlots';

import { AlertType } from 'constants/enums';

import { ReactComponent as AddSquareIcon } from 'assets/icons/add-square.svg';

import { useTranslation } from 'react-i18next';
import { standardAnimationProps } from '../../../constants/animations';
import SelectedSlot from '../../SelectedSlot/SelectedSlot';
import InterviewLinkForm from '../components/InterviewLinkForm/InterviewLinkForm';
import JoblioZoomToggle from '../components/JoblioZoomToggle/JoblioZoomToggle';
import styles from './ConfirmInterview.module.scss';

interface ConfirmInterviewProps {
  interviewRequest: InterviewDetails;
  onConfirmed: () => void;
  isLoading: boolean;
  onLoading: (isLoading: boolean) => void;
}

const ConfirmInterview: FC<ConfirmInterviewProps> = ({
  interviewRequest,
  onConfirmed,
  isLoading,
  onLoading
}) => {
  const { t } = useTranslation()

  const {
    form,
    selectedSlot,
    isJoblioZoomInterviewLink,
    canConfirmSlot,
    isValidUrl,
    handleChangeInterviewLink,
    handleConfirmSlot
  } = useInterviewConfirmation(interviewRequest, onConfirmed, onLoading);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [proposedSlots, setProposedSlots] = useState<InterviewSlotSerialized[]>(
    []
  );

  useEffect(() => {
    if (interviewRequest?.slots?.slotList.length) {
      const candidateTimeZone = interviewRequest.slots.timeZone;
      const rawSlots = interviewRequest.slots.slotList;

      // Convert slots to client time zone
      const convertedSlots = convertSlotsTimeZone(rawSlots, candidateTimeZone);

      setProposedSlots(convertedSlots);
    } else {
      setProposedSlots([]);
    }
  }, [interviewRequest]);

  return (
    <>
      <h2 className={styles.title}>
        {selectedSlot ? t('sidebar.application.interview.confirm.titleFinalize') : t('sidebar.application.interview.confirm.titleConfirm')}
      </h2>
      <motion.div layout>
        {!selectedSlot && (
          <motion.div {...standardAnimationProps}>
            <Alert
              type={AlertType.Info}
              message={t('sidebar.application.interview.confirm.confirmPreferredSlot')}
              className={styles.alert}
            />

            <Button
              type="primary"
              icon={<AddSquareIcon />}
              onClick={() => setIsModalOpen(true)}
              className={styles.btnFullWidth}
            >
              {t('sidebar.application.interview.confirm.selectSlot')}
            </Button>
          </motion.div>
        )}

        {selectedSlot && (
          <motion.div layout>
            <SelectedSlot
              slot={selectedSlot}
              onEdit={() => setIsModalOpen(true)}
              disabled={isLoading}
            />

            <InterviewLinkForm
              form={form}
              isVisible={!isJoblioZoomInterviewLink}
              onValidateUrl={isValidUrl}
              disabled={isLoading}
            />

            <JoblioZoomToggle
              onChange={handleChangeInterviewLink}
              isLoading={isLoading}
            />

            <Button
              type="primary"
              onClick={handleConfirmSlot}
              className={styles.btnFullWidth}
              disabled={!canConfirmSlot}
              loading={isLoading}
            >
              {t('common.confirm')}
            </Button>
          </motion.div>
        )}
      </motion.div>

      <InterviewSchedulerModal
        isOpen={isModalOpen}
        proposedSlots={proposedSlots}
        onClose={() => setIsModalOpen(false)}
        jobseekerName={interviewRequest?.application.jobseekerFullName}
      />
    </>
  );
};

export default ConfirmInterview;
